// Pure address-field presence checks. Returns a map of invalid field-keys
// (e.g. {name:true, zip:true}) — the caller maps keys to translated messages,
// so i18n stays in the component. Unit-testable without mounting a form.

/**
 * @param {{name?:string,street?:string,streetNo?:string,zip?:string,city?:string,countryId?:string}|null|undefined} addr
 * @param {{skipAddressLines?: boolean}} opts  skipAddressLines: only check the name
 *   (used for Packstation, where street/zip/city come from the station, not the form).
 * @returns {Record<string, true>} invalid field keys
 */
export function addressFieldErrors(addr, { skipAddressLines = false } = {}) {
    const invalid = {};
    // Name must contain at least a first + last name (two words).
    if (!addr?.name || addr.name.trim().split(/\s+/).filter(Boolean).length < 2) {
        invalid.name = true;
    }
    if (!skipAddressLines) {
        if (!addr?.street) invalid.street = true;
        if (!addr?.streetNo) invalid.streetNo = true;
        if (!addr?.zip) invalid.zip = true;
        if (!addr?.city) invalid.city = true;
        if (!addr?.countryId) invalid.countryId = true;
    }
    return invalid;
}

/**
 * Pflichtfelder einer GESPEICHERTEN Adresse, gleiche Regel wie saveCheckoutUser im Proxy.
 * decision: [1]
 *
 * @param {{name?:string,street?:string,streetNo?:string,zip?:string,city?:string,countryId?:string}|null|undefined} addr
 * @returns {boolean}
 */
export function savedAddressComplete(addr) {
    const filled = (v, min = 1) => typeof v === 'string' && v.trim().length >= min;
    return filled(addr?.name, 2) && filled(addr?.street) && filled(addr?.streetNo)
        && filled(addr?.zip, 4) && filled(addr?.city) && !!addr?.countryId;
}

/**
 * Rechnungsadresse und, falls eine eigene gewählt ist, Lieferadresse vollständig?
 * Dieselbe Auswahl der Adressen wie userStore.saveUser.
 *
 * @param {{hideShipping?:boolean, billingAddress?:object, shippingAddress?:object}|null|undefined} user
 * @returns {boolean}
 */
export function checkoutAddressesComplete(user) {
    if (!savedAddressComplete(user?.billingAddress)) return false;
    if (user?.hideShipping) return true;
    const s = user?.shippingAddress;
    const hasShippingData = !!(s?.name || s?.street || s?.zip || s?.city);
    return !hasShippingData || savedAddressComplete(s);
}

// ─── Google Address Validation (AMS) — frontend helpers ────────────────────
//
// The oxid-proxy persists Google Address Validation predictions as a JSON
// string in `Address.amsPredictions` with shape:
//
//     { verdict, address, responseId }
//
// `address` mirrors the Google AddressValidation response's `result.address`
// — most importantly `postalAddress` (regionCode / postalCode / locality /
// addressLines). We map that back onto Mount7's Address shape so the
// validation modal can show a side-by-side comparison and let the user
// adopt the suggestion with a single click.

/** AMS statuses that should NOT trigger the validation modal. */
export const AMS_STATUS_OK = 'address_correct';

/** AMS statuses that the frontend has to act on (modal prompt). */
export const AMS_STATUSES_NEEDS_REVIEW = [
    'address_needs_correction',
    'address_multiple_variants',
    'address_not_found',
];

/**
 * Returns true if the address requires user-facing review (i.e. the
 * validation modal should appear). Null / 'address_correct' → false.
 *
 * @param {string|null|undefined} status
 */
export function amsNeedsReview(status) {
    return !!status && AMS_STATUSES_NEEDS_REVIEW.includes(status);
}

// DHL Packstation: structural-validity mirror of the backend's
// validatePackstation() in oxid-proxy's AddressValidationService. Used to
// suppress stale-status modals on packstation rows whose shape is already
// fine — backend would flip them to address_correct on next save, but until
// then the cached MOJOAMSSTATUS would surface a misleading prompt.
export function isStructurallyValidPackstation(addr) {
    if (!addr) return false;
    if ((addr.street ?? '').trim().toLowerCase() !== 'packstation') return false;
    const stationNo = (addr.streetNo ?? '').trim();
    const postnummer = (addr.addInfo ?? '').trim();
    const country = (addr.countryCode ?? '').trim().toUpperCase();
    return (
        /^\d{1,4}$/.test(stationNo) &&
        /^\d{6,12}$/.test(postnummer) &&
        !!addr.zip && !!addr.city &&
        (country === 'DE' || country === 'AT')
    );
}

/**
 * Mount7-side review-gate used by User.svelte. Wraps amsNeedsReview but
 * also short-circuits valid packstations so a stale needs_correction
 * status doesn't fire a useless modal.
 */
export function addressNeedsReview(addr) {
    if (!addr) return false;
    if (!amsNeedsReview(addr.amsStatus)) return false;
    if (isStructurallyValidPackstation(addr)) return false;
    return true;
}

/**
 * Parse the raw `amsPredictions` JSON string. Returns null on bad input —
 * the modal then falls back to "no suggestion available" rendering.
 *
 * @param {string|null|undefined} raw
 * @returns {{verdict: object|null, address: object|null, responseId: string|null}|null}
 */
export function parsePredictions(raw) {
    if (!raw || typeof raw !== 'string') return null;
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return {
            verdict: parsed.verdict ?? null,
            address: parsed.address ?? null,
            responseId: parsed.responseId ?? null,
        };
    } catch (_) {
        return null;
    }
}

/**
 * Split a Google `addressLines[0]` token into Mount7's street / streetNo
 * shape. Convention used everywhere in the checkout:
 *   "Habsburgerstr. 10a"   → { street: 'Habsburgerstr.', streetNo: '10a' }
 *   "Hauptstraße 1"        → { street: 'Hauptstraße',    streetNo: '1' }
 *   "Im Eichwald 5-7"      → { street: 'Im Eichwald',    streetNo: '5-7' }
 *   "Hauptstraße"          → { street: 'Hauptstraße',    streetNo: ''   }
 *
 * Heuristic: pick the LAST whitespace-separated token IF it starts with a
 * digit; otherwise the whole line is the street. This matches what users
 * type in the AddressForm and how Endereco / Google return formatted
 * lines for DE/AT/CH. Will be imperfect for some PL/edge formats — the
 * modal still shows the suggestion verbatim, so the user can fall back
 * to "Adresse bearbeiten" if the split looks wrong.
 *
 * @param {string|null|undefined} line
 */
export function splitAddressLine(line) {
    if (!line || typeof line !== 'string') return {street: '', streetNo: ''};
    const trimmed = line.trim();
    if (!trimmed) return {street: '', streetNo: ''};
    const lastSpace = trimmed.lastIndexOf(' ');
    if (lastSpace === -1) {
        // Single token: assume street if it doesn't start with a digit.
        return /^\d/.test(trimmed)
            ? {street: '', streetNo: trimmed}
            : {street: trimmed, streetNo: ''};
    }
    const head = trimmed.slice(0, lastSpace).trim();
    const tail = trimmed.slice(lastSpace + 1).trim();
    if (/^\d/.test(tail)) {
        return {street: head, streetNo: tail};
    }
    return {street: trimmed, streetNo: ''};
}

/**
 * Map a parsed predictions object onto a Mount7-shaped Address suggestion.
 * Used by the validation modal: when the user clicks "Vorschlag übernehmen"
 * we feed these fields back into the Address store and re-trigger saveUser.
 *
 * countryId is NOT recoverable from Google's response (Google gives ISO
 * regionCode, OXID needs the 32-char oxcountry id). We therefore keep the
 * caller's original countryId and only override the geometry fields.
 *
 * @param {{verdict?: object|null, address?: object|null}|null} predictions
 * @returns {{street:string, streetNo:string, zip:string, city:string, addInfo:string, countryCode:string}|null}
 */
export function predictionToAddress(predictions) {
    const addr = predictions?.address;
    if (!addr) return null;
    const postal = addr.postalAddress;

    // Prefer Google's structured `addressComponents` over the
    // `postalAddress.addressLines` heuristic split: components carry the
    // pieces already typed (route / street_number / postal_code / locality /
    // country) which avoids guess-work for formats like "Sonnhalde invalid
    // 25" where the heuristic would join "Sonnhalde invalid" into street and
    // miss that Google actually CONFIRMED only "Sonnhalde" as the route.
    const components = Array.isArray(addr.addressComponents) ? addr.addressComponents : [];
    // Only consume CONFIRMED components. UNCONFIRMED_BUT_PLAUSIBLE etc.
    // mean Google guessed and we don't want to propagate that into a
    // "Vorschlag übernehmen" click. Empty fields fall through to the
    // caller's merge-with-original logic in applyPredictionAndResave.
    const confirmedText = (type) => {
        const c = components.find(x => x?.componentType === type);
        if (!c || c.confirmationLevel !== 'CONFIRMED') return '';
        return (c.componentName?.text ?? '').trim();
    };

    const streetFromComp    = confirmedText('route');
    const streetNoFromComp  = confirmedText('street_number');
    const zipFromComp       = confirmedText('postal_code');
    const countryFromComp   = confirmedText('country');

    // AT/DE locality false-positive guard (mirrors the backend in
    // AddressValidationService.mapVerdictToStatus). Google often rewrites the
    // locality to a colloquial Ortschaft that doesn't match the official
    // postal Ortsname for the PLZ (e.g. "Wals-Siezenheim" → "Wals" while
    // PLZ 5020 is "Salzburg"). When the PLZ is CONFIRMED and Google rewrote
    // the locality (replaced / spell-corrected / inferred), do NOT propagate
    // Google's city label — leave it empty so applyPredictionAndResave keeps
    // the user's original city. PLZ is the routing key in AT/DE.
    const localityComp = components.find(x => x?.componentType === 'locality');
    const postalComp   = components.find(x => x?.componentType === 'postal_code');
    // Drop only when the PLZ is the solid anchor: CONFIRMED *and* itself
    // untouched (not replaced/spellCorrected/inferred). If Google corrected
    // the PLZ too, this is a real correction the user should see — don't
    // suppress the city then.
    const postalConfirmedUnchanged = postalComp?.confirmationLevel === 'CONFIRMED'
        && !postalComp.replaced && !postalComp.spellCorrected && !postalComp.inferred;
    // `inferred` MUST trigger the drop too: the real Wals-Siezenheim→Wals
    // false-positive arrives as locality inferred=true (hasInferredComponents),
    // NOT replaced/spellCorrected (debugger task #8 evidence). Google rewrites
    // the locality to a colloquial Ortschaft that doesn't match the official
    // postal Ortsname for a CONFIRMED PLZ. Leaving city empty makes
    // applyPredictionAndResave keep the user's original — and a city that
    // already equals the original is a no-op (caller merges it back), so this
    // is safe even when Google only normalised an already-correct label.
    const localityRewritten = !!localityComp
        && (localityComp.replaced || localityComp.spellCorrected || localityComp.inferred);
    const dropCity = postalConfirmedUnchanged && localityRewritten;
    const cityFromComp      = dropCity ? '' : confirmedText('locality');

    // Fallback: if components are absent, fall back to the addressLines
    // heuristic the original implementation used. Without postalAddress
    // there's nothing to fall back to.
    if (!components.length && !postal) return null;

    let street = streetFromComp;
    let streetNo = streetNoFromComp;
    let addInfo = '';
    // Per-field fallback to the postalAddress heuristic. The previous
    // all-or-nothing guard (`!street && !streetNo`) lost the streetNo for the
    // common 'address_multiple_variants' shape where Google CONFIRMS the route
    // but leaves the street_number UNCONFIRMED: the confirmed street made the
    // guard skip, so the number never got filled and the suggestion silently
    // dropped it. Fill each missing piece independently from addressLines[0].
    if (postal && (!street || !streetNo)) {
        const lines = Array.isArray(postal.addressLines) ? postal.addressLines : [];
        const split = splitAddressLine(lines[0] || '');
        if (!street) street = split.street;
        if (!streetNo) streetNo = split.streetNo;
        addInfo = lines.slice(1).join(', ').trim();
    }

    // US/CH formats return the house number as a trailing "#NNNN" unit token
    // (route + subpremise both UNCONFIRMED → neither component is consumed, so
    // the addressLines heuristic leaves "North Street #2211" in the street).
    // 1) Always strip a trailing "#NNNN" unit from the street; adopt it as the
    //    streetNo when we don't have one yet. (Runs even with an empty streetNo
    //    — that was the bug: the guard below never fired, so the modal merged
    //    the original "2211" back and showed "North Street #2211 2211".)
    const unitMatch = street.match(/\s+#\s*([0-9][0-9a-zA-Z\-/]*)$/);
    if (unitMatch) {
        street = street.slice(0, unitMatch.index).trim();
        if (!streetNo) streetNo = unitMatch[1];
    }
    // 2) If the street still ends with the known street number, drop the dup.
    if (street && streetNo) {
        const escaped = streetNo.replace(/^#/, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        street = street.replace(new RegExp('\\s*#?' + escaped + '\\s*$'), '').trim();
    }

    return {
        street,
        streetNo,
        zip: (zipFromComp || postal?.postalCode || '').trim(),
        // dropCity: keep original city (empty suggestion → caller merges original).
        city: dropCity ? '' : (cityFromComp || postal?.locality || '').trim(),
        addInfo,
        countryCode: (countryFromComp || postal?.regionCode || '').trim().toUpperCase(),
    };
}

/**
 * True when the predicted address geometry differs from the original
 * (so the modal can label one side "Deine Eingabe" vs "Vorschlag"). We
 * compare normalised lowercase trimmed strings — Google often returns
 * "79100" vs "79100 " or "Freiburg" vs "Freiburg im Breisgau".
 *
 * @param {{street?:string,streetNo?:string,zip?:string,city?:string}|null|undefined} original
 * @param {{street?:string,streetNo?:string,zip?:string,city?:string}|null|undefined} suggestion
 */
export function differsFromOriginal(original, suggestion) {
    if (!original || !suggestion) return false;
    const norm = (s) => (s ?? '').toString().trim().toLowerCase();
    // Empty suggestion-fields mean "Google was not confident, keep
    // original" — those are NOT a real diff. Only count a field as
    // different when the suggestion has a non-empty value that disagrees
    // with the original.
    const diff = (sugg, orig) => {
        const s = norm(sugg);
        if (!s) return false;
        return s !== norm(orig);
    };
    return diff(suggestion.street, original.street)
        || diff(suggestion.streetNo, original.streetNo)
        || diff(suggestion.zip, original.zip)
        || diff(suggestion.city, original.city);
}
